import React, { useState, useEffect, useRef } from 'react';
import amazon from '../assets/trusted/amazonca.png';
import bestbuy from '../assets/trusted/bestbuy.png';
import canadianTire from '../assets/trusted/canadiantire.png';
import costco from '../assets/trusted/costco.png';
import jeancoutu from '../assets/trusted/jeancoutu.png';
import lawtonsdrugs from '../assets/trusted/lawtonsdrugs.png';
import loblaws from '../assets/trusted/loblaws.png';
import londonDrugs from '../assets/trusted/londondrugs.png';
import mckesson from '../assets/trusted/mckesson.png';
import mountainEquipment from '../assets/trusted/mountainequipcoop.png';
import overweiteafoods from '../assets/trusted/overwaiteafoods.png';
import safeway from '../assets/trusted/safeway.png';
import shoppersDrugMart from '../assets/trusted/shoppersdrugmart.png';
import sobeys from '../assets/trusted/sobeys.png';
import sportchek from '../assets/trusted/sportchek.png';
import tgp from '../assets/trusted/tgp.png';
import truserv from '../assets/trusted/truserv.png';
import valueddrugmart from '../assets/trusted/valuedrugmart.png';
import walmart from '../assets/trusted/walmart.png';
import wellca from '../assets/trusted/wellca.png';


// New TypewriterText component
const TypewriterText = ({ text, isActive, delay = 0 }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const hasCompleted = useRef(false);

  useEffect(() => {
    if (!isActive) {
      if (!hasCompleted.current) {
        setDisplayedText('');
        setCurrentIndex(0);
      }
      return;
    }

    if (!hasCompleted.current && currentIndex < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, currentIndex + 1));
        setCurrentIndex(currentIndex + 1);
      }, currentIndex === 0 ? delay : 90);

      return () => clearTimeout(timeout);
    }
  }, [isActive, currentIndex, text, delay]);

  useEffect(() => {
    if (currentIndex === text.length) {
      hasCompleted.current = true;
    }
  }, [currentIndex, text]);

  return (
    <span>
      {displayedText}
      {isActive && currentIndex < text.length && (
        <span className="animate-pulse">|</span>
      )}
    </span>
  );
};

const TrustedBy = () => {
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);
  const logos = [
    amazon, bestbuy, canadianTire, costco, jeancoutu, lawtonsdrugs, loblaws,
    londonDrugs, mckesson, mountainEquipment, overweiteafoods, safeway,
    shoppersDrugMart, sobeys, sportchek, tgp, truserv, valueddrugmart, walmart, wellca,
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const headingText = "Brands Who Trust Us";
  // const subheadingText = "Industry Leaders Across the Globe";
  const headingTypingDuration = headingText.length * 90;
  const subheadingDelay = headingTypingDuration + 300; // 300ms pause after heading

  return (
    <section
      ref={sectionRef}
      className="w-full py-8 px-2 sm:px-4 text-white relative overflow-hidden"
    >
      <div className="text-center mb-6 min-h-[120px] sm:min-h-[150px]">
        <h2 className="text-2xl sm:text-4xl font-bold lg:text-6xl mb-2 sm:mb-4">
          {inView && (
            <TypewriterText
              text={headingText}
              isActive={inView}
              delay={0}
            />
          )}
        </h2>
        {/* <p className="text-base sm:text-lg lg:text-4xl py-3 sm:py-5">
          Industry Leaders Across the Globe
        </p> */}
      </div>

      {/* Marquee Container */}
      <div className="relative overflow-hidden">
        <div className="marquee-container">
          <div className="marquee-content">
            {/* First set of logos */}
            {logos.map((logo, index) => (
              <div
                key={`first-${index}`}
                className="marquee-item flex-shrink-0 mx-3 sm:mx-6 flex items-center justify-center"
              >
                <img
                  src={logo}
                  alt={`Trusted brand ${index + 1}`}
                  className="h-8 sm:h-12 lg:h-16 w-auto object-contain filter brightness-0 invert"
                />
              </div>
            ))}
            {/* Second set of logos for seamless loop */}
            {logos.map((logo, index) => (
              <div
                key={`second-${index}`}
                className="marquee-item flex-shrink-0 mx-3 sm:mx-6 flex items-center justify-center"
              >
                <img
                  src={logo}
                  alt={`Trusted brand ${index + 1}`}
                  className="h-8 sm:h-12 lg:h-16 w-auto object-contain filter brightness-0 invert"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .marquee-container {
          width: 100%;
          overflow: hidden;
          position: relative;
        }
        
        .marquee-content {
          display: flex;
          animation: marquee 30s linear infinite;
          width: max-content;
        }
        
        .marquee-item {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 120px;
          height: 60px;
        }
        
        @keyframes marquee {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        
        /* Pause animation on hover */
        .marquee-container:hover .marquee-content {
          animation-play-state: paused;
        }
        
        /* Responsive adjustments */
        @media (max-width: 640px) {
          .marquee-content {
            animation-duration: 20s;
          }
          
          .marquee-item {
            min-width: 100px;
            height: 50px;
          }
        }
        
        @media (max-width: 480px) {
          .marquee-content {
            animation-duration: 15s;
          }
          
          .marquee-item {
            min-width: 80px;
            height: 40px;
          }
        }
      `}</style>
    </section>
  );
};

export default TrustedBy;